// src/pages/TrackCreation/Step10.tsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-geometryutil";
import { MapContainer, TileLayer, Polyline, Marker } from "react-leaflet";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import polyline from "@mapbox/polyline";
import L from "leaflet";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { DropdownOption, MapContentProps, MarkerType } from "../../../../types/screensTypes";
import { MergedWaypoint, TemporaryWaypoint, Waypoint } from "../../../../types/types";
import {
    deleteWaypointAPI,
    getGeocode,
    saveTemporaryWaypoint,
    applyUpdatesToTrack,
    getWaypointsForTrack,
    resetTrack,
} from "../../../../api";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import Button from "../../../../components/ui/Button";
import Icon from "../../../../components/common/Icon";
import CustomMarker from "../../../../components/map/MapMarker";
import WaypointList from "../../../../components/lists/WaypointList";
import { MapInitializer } from "../../../../utils/mapInitializer";
import shell from "../createShell.module.css";
import mapStyles from "../createMap.module.css";
import stepStyles from "../createStep.module.css";
import AudioPlayerRewind from "../../../../components/common/AudioPlayer";
import VideoPlayerRewind from "../../../../components/common/VideoPlayer";
import HorizontalSeparator from "../../../../components/common/HorizontalSeparator";
import { getTrackLanguages } from "../../../../api/track";
import { getWaypointById } from "../../../../api/waypoint";
import { Tooltip } from "@mui/material";
import ExportButton from "../../../analytics/components/ExportButton";
import { buildWaypointExportRows, exportWaypointsToExcel } from "../../../../utils/exportWaypointsToExcel";
import audioFooter from "../../../../components/common/AudioPlayerFooter.module.css";

// Local helper type to track loop sections
type MergedWaypointWithSection = MergedWaypoint & { section?: number };
type CachedAudio = { url: string; title: string; position: number; mediaType?: "AUDIO" | "VIDEO" };

export const MapContent: React.FC<MapContentProps> = ({ navigateToNext, navigateToContinue }) => {
    const { trackData } = useTrackCreation();
    const { data } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const refreshWaypoints = useCallback(async () => {
        if (trackData?.trackId) {
            await getWaypointTrackById(trackData.trackId);
        }
    }, [trackData?.trackId]);

    const [address, setAddress] = useState<string>("");
    const [coordinates, setCoordinates] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [wptType, setWptType] = useState<string | null>(null);
    const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
    const [temporaryWaypoints, setTemporaryWaypoints] = useState<TemporaryWaypoint[]>([]);
    const [cityName, setCityName] = useState<string | null>(null);
    const [map, setMap] = useState<L.Map | null>(null);
    const [combinedWaypoints, setCombinedWaypoint] = useState<MergedWaypoint[]>([]);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [, setIsGenerateButtonDisabled] = useState<boolean>(true);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [polylineDecoded, setPolylineDecoded] = useState<L.LatLng[] | null>(null);
    const hasExecuted = useRef<boolean>(false);

    // Track if there are local unsaved changes (set when EditableWaypointCard saves)
    const [hasLocalUnsavedChanges, setHasLocalUnsavedChanges] = useState(false);

    // Check if there are unsaved changes:
    // - Either from temporary waypoints loaded from server
    // - Or from local edits made in this session
    const hasUnsavedChanges = temporaryWaypoints.length > 0 || hasLocalUnsavedChanges;

    // Callback for EditableWaypointCard to notify when a change is saved
    const onUnsavedChange = useCallback(() => {
        setHasLocalUnsavedChanges(true);
    }, []);
    // Bus playback state
    const [busPosition, setBusPosition] = useState<L.LatLng | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [busSpeed, setBusSpeed] = useState<number>(50); // km/h
    const [followBus, setFollowBus] = useState<boolean>(true);
    const [showSimulator, setShowSimulator] = useState<boolean>(false);
    const animationRef = useRef<number>();
    const polylinePointsRef = useRef<L.LatLng[]>([]);
    const cumDistancesRef = useRef<number[]>([]); // cumulative distance from start (km) at each point
    const totalDistanceKmRef = useRef<number>(0);
    const playStartTimeRef = useRef<number>(0);
    const pausedDistanceKmRef = useRef<number>(0);
    const currentDistanceKmRef = useRef<number>(0);
    const searchIdxRef = useRef<number>(1);
    const [startWaypointId, setStartWaypointId] = useState<string | null>(null);

    // Language + audio
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [exportingWaypoints, setExportingWaypoints] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [mp3File, setMp3File] = useState<string | null>(null);
    const [audioTitle, setAudioTitle] = useState<string>("");
    const [simulatorMediaType, setSimulatorMediaType] = useState<"AUDIO" | "VIDEO">("AUDIO");
    const nextWaypointIdxRef = useRef<number>(0);
    const currentSectionRef = useRef<number>(0);
    const [currentSection, setCurrentSection] = useState<number>(0);
    // Section metadata (mirrors mobile WaypointManager sections): the id of the
    // last waypoint (by position) of each section, and the ordered list of section
    // ids. Used to advance to the next section only when the current section's last
    // waypoint is consumed, exactly like iOS/Android.
    const sectionLastIdRef = useRef<Map<number, string>>(new Map());
    const sectionIdsRef = useRef<number[]>([]);
    const triggeredIdsRef = useRef<Set<string>>(new Set());
    // null = fetched but no media (avoids re-hitting the API forever in the prefetch zone)
    const audioUrlCacheRef = useRef<Map<string, CachedAudio | null>>(new Map());
    // Keep the in-flight promise so callers can await the same request instead of
    // firing a new one (prevents requestAnimationFrame from spamming the API).
    const audioPrefetchInFlightRef = useRef<Map<string, Promise<CachedAudio | null>>>(new Map());
    // Media lock: mirrors the mobile apps' behavior where a playing waypoint (audio
    // OR video) blocks the next content waypoint until it ends/closes. Invisible /
    // no-media waypoints are still consumed so sections & loops keep advancing.
    const mediaBusyRef = useRef<boolean>(false);

    // Dropdown options for waypoint type
    const dropdownOptionsWaypointType: DropdownOption[] = [
        { label: t("content"), value: "FIXED" },
        { label: t("crossingPoint"), value: "INVISIBLE" },
    ];

    const deleteWaypoint = async (id: string): Promise<void> => {
        setLoadingSave(true);
        const trackId = trackData.trackId;
        const token = localStorage.getItem("token") || "";
        if (!trackId || !token || !id) {
            setLoadingSave(false);
            return;
        }

        try {
            await deleteWaypointAPI({ token, trackId, waypointId: id });
            await getWaypointTrackById(trackId);
            setLoadingSave(false);
        } catch (error) {
            setLoadingSave(false);
        }
    };

    const handleAddWaypoint = async (): Promise<void> => {
        let latitude: string | undefined;
        let longitude: string | undefined;

        const trackId = trackData.trackId;

        if (!trackId) {
            return;
        }
        const temporaryId = uuidv4();
        if (coordinates.trim() !== "") {
            const parts = coordinates.split(",");
            if (parts.length >= 2) {
                latitude = parts[0];
                longitude = parts[1];
            }
        } else if (address.trim() !== "") {
            const geocode = await getGeocode({
                address: (address + " " + (cityName || "")).trim(),
            });
            if (geocode) {
                latitude = geocode.lat;
                longitude = geocode.lon;
            } else {
                window.alert(t("addressNotFound"));
            }
        }

        // Check if a waypoint with the same position already exists
        const existingWaypointWithSamePosition = combinedWaypoints.find(
            (waypoint) => waypoint.position === parseInt(position),
        );

        if (existingWaypointWithSamePosition) {
            const allWaypointsPositionToChange = combinedWaypoints.filter(
                (waypoint) => waypoint.position >= parseInt(position),
            );

            allWaypointsPositionToChange.map((waypoint, index) => {
                const newPosition = parseInt(position) + index + 1; // New position
                const isTemp = (waypoint as any).status ? true : false;
                if (waypoint.position !== newPosition) {
                    const tempWaypoint: MergedWaypoint = {
                        ...waypoint,
                        position: newPosition,
                        internalId: (waypoint as any).internalId ? (waypoint as any).internalId : uuidv4(),
                        waypointId:
                            isTemp && (waypoint as any).waypointId
                                ? (waypoint as any).waypointId
                                : isTemp
                                  ? null
                                  : (waypoint as any).id,
                    } as any;
                    // Save temporary waypoint
                    saveTemporaryWaypoint({
                        token: localStorage.getItem("token") || "",
                        internalId: (tempWaypoint as any).internalId!,
                        trackId: (waypoint as any).trackId || "",
                        waypointId: (tempWaypoint as any).waypointId ? (tempWaypoint as any).waypointId : null,
                        latitude: (waypoint as any).point.lat || 0.0,
                        longitude: (waypoint as any).point.lng || 0.0,
                        address: null,
                        position: newPosition,
                    });
                    return tempWaypoint;
                }
                return waypoint;
            });
        }

        if (latitude && longitude && position && temporaryId && wptType) {
            const token = localStorage.getItem("token") || "";
            try {
                await saveTemporaryWaypoint({
                    token,
                    internalId: temporaryId,
                    trackId: trackId,
                    waypointId: null,
                    latitude: parseFloat(latitude.trim()),
                    longitude: parseFloat(longitude.trim()),
                    address: address,
                    position: parseInt(position),
                    type: wptType ? wptType : "FIXED",
                });
                await getWaypointTrackById(trackId);
                // Clear form after successful save
                setAddress("");
                setCoordinates("");
                setPosition("");
                setWptType(null);
            } catch (error) {
                console.error("Error saving waypoint:", error);
            }
        } else {
            setShowErrors(true);
            setTimeout(() => {
                setShowErrors(false);
            }, 3000);
            return;
        }
    };

    const handleMarkerDragEnd = async (newPosition: { lat: number; lng: number }, id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;

        if (!trackId || !token) {
            return;
        }

        const { lat, lng } = newPosition;

        // First, check if the waypoint exists in the temporary waypoints.
        const savedWaypoint = temporaryWaypoints.find((wp) => (wp as any).id === id || (wp as any).waypointId === id);

        // Use the existing internal ID, or generate a new one
        const temporaryId = savedWaypoint ? (savedWaypoint as any).internalId : uuidv4();

        const waypointId = savedWaypoint ? (savedWaypoint as any).waypointId : id;

        await saveTemporaryWaypoint({
            token,
            internalId: temporaryId, // Use the saved waypoint's id as the internal id.
            trackId,
            waypointId: waypointId,
            latitude: lat,
            longitude: lng,
        });

        await getWaypointTrackById(trackId);
    };

    const saveModificationToTrack = async (): Promise<void> => {
        setLoadingSave(true);
        const trackId = trackData.trackId;
        const token = localStorage.getItem("token");
        if (!token || !trackId) {
            setLoadingSave(false);
            return;
        }
        await applyUpdatesToTrack({ token, trackId, excludeTriggerMode: "MANUAL" });
        await getWaypointTrackById(trackId);
        // Reset the local unsaved changes flag after applying
        setHasLocalUnsavedChanges(false);
        setLoadingSave(false);
    };

    const getWaypointTrackById = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token") || "";
        const response = await getWaypointsForTrack({ token, trackId: id });
        if (response) {
            const tempWpts: TemporaryWaypoint[] = response.temporaryWaypoints;
            const wpts: Waypoint[] = response.waypoints;
            if (response.polyline && response.polyline !== "") {
                const decodedPolyline = polyline.decode(response.polyline);
                const polylineLatLngs = decodedPolyline.map(([lat, lng]: [number, number]) => L.latLng(lat, lng));
                setPolylineDecoded(polylineLatLngs);
            }
            const isMapWaypoint = (wpt: any) => wpt.triggerMode !== "MANUAL" && wpt.triggerMode !== "IMAGE_SCAN";
            setTemporaryWaypoints(tempWpts.filter(isMapWaypoint));
            setWaypoints(wpts.filter(isMapWaypoint));
        }
    };

    const rsetTrack = async (): Promise<void> => {
        const trackId = trackData.trackId;
        const token = localStorage.getItem("token");

        if (!token || !trackId) {
            return;
        }
        setLoadingSave(true);
        await resetTrack({ token, trackId });
        await getWaypointTrackById(trackId);
        setLoadingSave(false);
    };

    const [fieldErrors, setFieldErrors] = useState({
        address: false,
        coordinates: false,
        position: false,
        wptType: false,
    });

    useEffect(() => {
        const errors = {
            address: !address && !coordinates, // error if both are empty
            coordinates: !coordinates && !address,
            position: !position,
            wptType: !wptType,
        };
        setFieldErrors(errors);
        const hasErrors = Object.values(errors).some((error) => error);
        setIsGenerateButtonDisabled(hasErrors);
    }, [address, coordinates, position, wptType]);

    useEffect(() => {
        if (trackData && trackData.trackId) {
            const trackId = trackData.trackId;
            getWaypointTrackById(trackId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId]);

    useEffect(() => {
        if (!hasExecuted.current && map) {
            if (waypoints && waypoints.length > 0) {
                const latLngBounds = L.latLngBounds([]);
                waypoints.forEach((waypoint) => {
                    latLngBounds.extend([waypoint.point?.lat ?? 0, waypoint.point?.lng ?? 0]);
                });
                map.fitBounds(latLngBounds);
                hasExecuted.current = true;
            }
        }
    }, [map, waypoints]);

    // Fetch existing track languages for dropdown
    const languagesAI = (data as any)?.languagesAI as DropdownOption[] | undefined;
    const languagesAIJsonFormatted: DropdownOption[] = useMemo(() => languagesAI || [], [languagesAI]);

    // Persist and hydrate controls for better UX
    useEffect(() => {
        const savedLang = localStorage.getItem("map.selectedLanguage");
        const savedSpeed = localStorage.getItem("map.busSpeed");
        if (savedLang) setSelectedLanguage(savedLang);
        if (savedSpeed) setBusSpeed(Number(savedSpeed));
    }, []);

    useEffect(() => {
        if (selectedLanguage) localStorage.setItem("map.selectedLanguage", selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        localStorage.setItem("map.busSpeed", String(busSpeed));
    }, [busSpeed]);

    const getTrackLanguagesAPI = useCallback(async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        const response = await getTrackLanguages({ token, id: trackId });
        const languages = response?.formattedLanguages || [];
        const notPublishedLanguages = response?.formattedContentLocalizedLanguages || [];
        const arrayLang: string[] = [];
        for (let i = 0; i < languages.length; i++) {
            const lang = languages[i];
            const langObj = languagesAIJsonFormatted.find((l: DropdownOption) => l.value === lang);
            if (langObj) arrayLang.push(langObj.label);
        }
        for (let i = 0; i < notPublishedLanguages.length; i++) {
            const lang = notPublishedLanguages[i];
            const langObj = languagesAIJsonFormatted.find((l: DropdownOption) => l.value === lang);
            if (langObj) arrayLang.push(langObj.label);
        }
        setExistingLanguages(arrayLang);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languagesAIJsonFormatted, trackData.trackId]);

    const filteredLanguageOptions = useMemo<DropdownOption[]>(() => {
        return languagesAIJsonFormatted.filter((lang) => existingLanguages.includes(lang.label));
    }, [languagesAIJsonFormatted, existingLanguages]);

    useEffect(() => {
        getTrackLanguagesAPI();
        const cities = (data as any)?.locationCityOptions;
        const trackCity = (trackData as any)?.city;
        const cityFilter = cities ? cities.filter((city: any) => city.value === trackCity) : [];
        const city = cityFilter && cityFilter.length > 0 ? cityFilter[0] : null;
        if (city) {
            setCityName(city.label);
        }
    }, [data, trackData, getTrackLanguagesAPI]);

    // Build combined waypoints and assign sections
    const assignSections = (wpts: MergedWaypoint[]): MergedWaypointWithSection[] => {
        let currentSection = 0;
        return wpts.map((wpt) => {
            const res: MergedWaypointWithSection = {
                ...wpt,
                section: currentSection,
            };
            if ((wpt as any).isReferenceStopForReturnLoop) {
                currentSection += 1;
            }
            return res;
        });
    };

    useEffect(() => {
        const combWaypoints: MergedWaypoint[] = [
            ...waypoints,
            ...temporaryWaypoints.filter(
                (tempWaypoint) => !waypoints.some((waypoint) => waypoint.id === tempWaypoint.waypointId),
            ),
        ].map((waypoint: Waypoint | TemporaryWaypoint): MergedWaypoint => {
            const tempWaypoint = temporaryWaypoints.find((temp) => temp.waypointId === waypoint.id);

            const idMerged = tempWaypoint
                ? tempWaypoint.waypointId
                    ? tempWaypoint.waypointId
                    : tempWaypoint.internalId
                : (waypoint.id ?? "");

            // Default values to ensure all fields are present
            const defaultWaypoint: Waypoint = {
                id: waypoint.id ?? tempWaypoint?.waypointId ?? "",
                internalName: "internalName" in waypoint ? waypoint.internalName : (tempWaypoint?.name ?? ""),
                type: waypoint.type ?? tempWaypoint?.type ?? "",
                position: waypoint.position ?? tempWaypoint?.position ?? 0,
                closeToAnother: "closeToAnother" in waypoint ? waypoint.closeToAnother : false,
                inLoop: "inLoop" in waypoint ? waypoint.inLoop : (tempWaypoint?.inLoop ?? false),
                isStraightLine:
                    "isStraightLine" in waypoint ? waypoint.isStraightLine : (tempWaypoint?.isStraightLine ?? false),
                isStop: "isStop" in waypoint ? waypoint.isStop : false,
                isReferenceStopForReturnLoop:
                    "isReferenceStopForReturnLoop" in waypoint
                        ? waypoint.isReferenceStopForReturnLoop
                        : (tempWaypoint?.isReferenceStopForReturnLoop ?? false),
                shouldStartIfDoubt: "shouldStartIfDoubt" in waypoint ? waypoint.shouldStartIfDoubt : false,
                startDistance: "startDistance" in waypoint ? waypoint.startDistance : null,
                trackId: waypoint.trackId ?? tempWaypoint?.trackId ?? "",
                busId: "busId" in waypoint ? waypoint.busId : null,
                wptPtId: "wptPtId" in waypoint ? waypoint.wptPtId : "",
                waypointHohoStopId: "waypointHohoStopId" in waypoint ? waypoint.waypointHohoStopId : null,
                waypointHohoStop: "waypointHohoStop" in waypoint ? waypoint.waypointHohoStop : ({} as any),
                waypointLocalized: "waypointLocalized" in waypoint ? waypoint.waypointLocalized : [],
                point:
                    "point" in waypoint
                        ? waypoint.point
                        : {
                              lat: (waypoint as any).latitude ?? 0,
                              lng: (waypoint as any).longitude ?? 0,
                              id: "tempPoint",
                          },
                mediaType:
                    ("mediaType" in waypoint ? (waypoint as any).mediaType : null) ??
                    (tempWaypoint as any)?.mediaType ??
                    "AUDIO",
                triggerMode:
                    ("triggerMode" in waypoint ? (waypoint as any).triggerMode : null) ??
                    (tempWaypoint as any)?.triggerMode ??
                    "GPS",
            };

            const merged: MergedWaypoint = {
                ...defaultWaypoint,
                ...(tempWaypoint as any),
                latitude:
                    (tempWaypoint as any)?.latitude ??
                    ("point" in waypoint ? waypoint.point?.lat : (waypoint as any).latitude),
                longitude:
                    (tempWaypoint as any)?.longitude ??
                    ("point" in waypoint ? waypoint.point?.lng : (waypoint as any).longitude),
                id: idMerged as any,
                internalId:
                    (tempWaypoint as any)?.internalId ??
                    ("internalId" in waypoint ? (waypoint as any).internalId : undefined),
            } as any;

            return merged;
        });

        // --- Overlap Detection ---
        const overlappingIds = new Set<string>();
        for (let i = 0; i < combWaypoints.length; i++) {
            for (let j = i + 1; j < combWaypoints.length; j++) {
                const wp1 = combWaypoints[i] as any;
                const wp2 = combWaypoints[j] as any;
                const point1 = L.latLng(wp1.latitude ?? 0, wp1.longitude ?? 0);
                const point2 = L.latLng(wp2.latitude ?? 0, wp2.longitude ?? 0);
                const distance = point1.distanceTo(point2);
                const radius1 = wp1.startDistance || 0;
                const radius2 = wp2.startDistance || 0;

                if (distance < radius1 + radius2) {
                    overlappingIds.add(wp1.id);
                    overlappingIds.add(wp2.id);
                }
            }
        }

        const mergedWithOverlapFlag = combWaypoints.map((wpt: any) => ({
            ...wpt,
            isOverlapping: overlappingIds.has(wpt.id),
        }));

        const withSection = assignSections(mergedWithOverlapFlag.sort((a: any, b: any) => a.position - b.position));

        // Record, per section, the id of its last waypoint (highest position) and
        // the ordered list of section ids. Mirrors mobile's sections so we only
        // advance to the next section when the current one's last waypoint plays.
        const lastIdBySection = new Map<number, string>();
        const lastPosBySection = new Map<number, number>();
        withSection.forEach((w: any) => {
            const s = typeof w.section === "number" ? w.section : 0;
            const pos = typeof w.position === "number" ? w.position : 0;
            if (!lastPosBySection.has(s) || pos > (lastPosBySection.get(s) as number)) {
                lastPosBySection.set(s, pos);
                lastIdBySection.set(s, w.id);
            }
        });
        sectionLastIdRef.current = lastIdBySection;
        sectionIdsRef.current = Array.from(lastIdBySection.keys()).sort((a, b) => a - b);

        // --- Final set
        setCombinedWaypoint(withSection as unknown as MergedWaypoint[]);
    }, [waypoints, temporaryWaypoints]);

    const handleMapReady = useCallback((mapInstance: L.Map) => {
        // set only once; ignore on future renders
        setMap((prev) => prev ?? mapInstance);
    }, []);

    // --- Audio trigger helpers
    const setSectionValue = (section: number) => {
        currentSectionRef.current = section;
        setCurrentSection(section);
    };

    // Next section id, wrapping back to the first one at the end (return loop).
    const nextSectionId = (current: number): number => {
        const ids = sectionIdsRef.current;
        if (ids.length === 0) return current;
        const idx = ids.indexOf(current);
        if (idx === -1) return ids[0];
        return idx < ids.length - 1 ? ids[idx + 1] : ids[0];
    };

    // Faithful port of the mobile section/loop logic (iOS WaypointManager /
    // Android AutomaticWaypointPlayerImpl):
    //  - only waypoints of the current section are eligible (loop points);
    //  - a non-loop waypoint selects its own section;
    //  - the section advances to the next one ONLY when the CURRENT section's
    //    last waypoint (by position, i.e. its reference stop) is consumed.
    // Crucially, passing a later section's reference stop — e.g. the return-loop
    // end point (35) that physically sits back near the start between 1 and 2 —
    // must NOT jump the section, unlike the previous buggy implementation.
    const shouldPlay = (wpt: MergedWaypointWithSection): boolean => {
        const waypointSection = typeof wpt.section === "number" ? wpt.section : currentSectionRef.current;
        const isInLoop = Boolean((wpt as any).inLoop);
        const id = (wpt as any).id as string;

        // Non-loop waypoints select their own section (mobile: getIndexSectionOfWaypoint).
        if (!isInLoop && waypointSection !== currentSectionRef.current) {
            setSectionValue(waypointSection);
        }

        // Only waypoints belonging to the current section are played.
        if (waypointSection !== currentSectionRef.current) {
            return false;
        }

        // Advance to the next section only when the current section's last
        // waypoint is consumed (mobile: getIndexSectionAlgorithm end-of-section).
        if (sectionLastIdRef.current.get(currentSectionRef.current) === id) {
            setSectionValue(nextSectionId(currentSectionRef.current));
        }

        return true;
    };

    const fetchWaypointAudio = useCallback(
        async (id: string): Promise<CachedAudio | null> => {
            const token = localStorage.getItem("token");
            if (!token || !selectedLanguage) return null;
            try {
                const waypointData: any = await getWaypointById({
                    token,
                    id,
                    language: selectedLanguage,
                });
                if (!waypointData) return null;

                // Resolve mediaType: prefer temp, then persisted, default AUDIO
                const resolvedMediaType: "AUDIO" | "VIDEO" =
                    waypointData?.waypointTemporary?.mediaType === "VIDEO" ||
                    waypointData?.waypoint?.mediaType === "VIDEO"
                        ? "VIDEO"
                        : "AUDIO";

                // Try temp localized first
                const tempLoc = waypointData?.waypointTemporary?.TemporaryWaypointLocalized;
                if (tempLoc && tempLoc.length > 0) {
                    if (resolvedMediaType === "VIDEO") {
                        const videoUrl = tempLoc[0]?.videoUrl as string | null;
                        // Fallback to persisted videoUrl
                        const persistedVideoUrl = waypointData?.waypoint?.waypointLocalized?.[0]?.videoUrl as
                            | string
                            | null;
                        const url = videoUrl || persistedVideoUrl;
                        if (url) {
                            const nameFromTemp = tempLoc[0]?.name || tempLoc[0]?.title || "";
                            const title = nameFromTemp || (url?.split("/").pop() || "").split("?")[0] || t("unnamed");
                            return {
                                url,
                                title,
                                position: waypointData?.waypointTemporary?.position,
                                mediaType: "VIDEO",
                            };
                        }
                    } else if (tempLoc[0]?.audioUrl) {
                        const nameFromTemp = tempLoc[0]?.name || tempLoc[0]?.title || "";
                        const url = tempLoc[0].audioUrl as string;
                        const title = nameFromTemp || (url?.split("/").pop() || "").split("?")[0] || t("unnamed");
                        return { url, title, position: waypointData?.waypointTemporary?.position, mediaType: "AUDIO" };
                    }
                }

                // Fallback to persisted waypoint localized
                const loc = waypointData?.waypoint?.waypointLocalized;
                if (resolvedMediaType === "VIDEO") {
                    const url = loc && loc.length > 0 ? (loc[0]?.videoUrl as string | null) : null;
                    if (url) {
                        const name = loc[0]?.name || loc[0]?.title || "";
                        const title = name || (url?.split("/").pop() || "").split("?")[0] || t("unnamed");
                        return { url, title, position: waypointData?.waypoint?.position, mediaType: "VIDEO" };
                    }
                } else {
                    const file = loc && loc.length > 0 ? loc[0]?.audiotrack?.file : null;
                    const name = loc && loc.length > 0 ? loc[0]?.name || loc[0]?.title || "" : "";
                    if (file) {
                        const title = name || (file?.split("/").pop() || "").split("?")[0] || t("unnamed");
                        return { url: file, title, position: waypointData?.waypoint?.position, mediaType: "AUDIO" };
                    }
                }

                return null;
            } catch (e) {
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedLanguage],
    );

    const ensureAudioPrefetched = useCallback(
        (id: string): Promise<CachedAudio | null> => {
            if (audioUrlCacheRef.current.has(id)) {
                return Promise.resolve(audioUrlCacheRef.current.get(id) ?? null);
            }
            // Reuse the in-flight request so requestAnimationFrame (~60fps) can't
            // spam get-waypoint-by-id (WAF rate-limit risk), while still resolving
            // to the real value for every caller once the fetch completes.
            const inFlight = audioPrefetchInFlightRef.current.get(id);
            if (inFlight) return inFlight;

            const request = fetchWaypointAudio(id)
                .then((audio) => {
                    audioUrlCacheRef.current.set(id, audio);
                    return audio;
                })
                .finally(() => {
                    audioPrefetchInFlightRef.current.delete(id);
                });
            audioPrefetchInFlightRef.current.set(id, request);
            return request;
        },
        [fetchWaypointAudio],
    );

    const triggerNextWaypointAudio = useCallback(
        (busPos: L.LatLng) => {
            for (let i = 0; i < combinedWaypoints.length; i++) {
                const wpt = combinedWaypoints[i] as MergedWaypointWithSection;
                const id = (wpt as any).id as string;
                const lat = (wpt as any).latitude as number | undefined;
                const lng = (wpt as any).longitude as number | undefined;
                if (lat == null || lng == null) continue;

                // Invisible / crossing points are never played on mobile (filtered by
                // type): they only advance sections & loops. Mirror that here so they
                // never play audio nor hold the media lock, even if they happen to
                // carry a localized audioUrl.
                const isInvisible = ((wpt as any).type as string) === "INVISIBLE";

                const dist = busPos.distanceTo(L.latLng(lat, lng));
                const radius = (wpt as any).startDistance ?? 20;

                // Prefetch when getting close (deduped via cache + in-flight set).
                // No media to fetch for invisible points.
                if (!isInvisible && !triggeredIdsRef.current.has(id) && dist <= radius * 3) {
                    void ensureAudioPrefetched(id);
                }

                // Trigger inside bubble
                if (!triggeredIdsRef.current.has(id) && dist <= radius) {
                    const isResolved = audioUrlCacheRef.current.has(id);
                    const cached = audioUrlCacheRef.current.get(id) ?? null;
                    // No-media point = invisible, or resolved with no audio/video.
                    // These are always consumable and never take/keep the lock.
                    const isNoMediaPoint = isInvisible || (isResolved && cached == null);

                    // While a waypoint is playing, don't start/queue another content
                    // waypoint (it would overwrite the player). We also don't mark it
                    // triggered, so it can still fire later IF the bus is still inside
                    // its bubble once the current media ends — exactly like mobile,
                    // where leaving the zone during playback means the point is missed.
                    if (mediaBusyRef.current && !isNoMediaPoint) {
                        continue;
                    }

                    if (shouldPlay(wpt) && selectedLanguage) {
                        triggeredIdsRef.current.add(id);
                        nextWaypointIdxRef.current = Math.min(nextWaypointIdxRef.current, i + 1);
                        if (isInvisible) {
                            // Consumed only: advances section/loop, no playback, no lock.
                        } else if (cached) {
                            mediaBusyRef.current = true;
                            setMp3File(cached.url);
                            setAudioTitle(cached.position + " " + cached.title || t("unnamed"));
                            setSimulatorMediaType(cached.mediaType ?? "AUDIO");
                        } else if (!isResolved) {
                            // Not cached yet: await the (possibly in-flight) request so
                            // we still play once it resolves, without a duplicate call.
                            void ensureAudioPrefetched(id).then((audio) => {
                                if (audio) {
                                    mediaBusyRef.current = true;
                                    setMp3File(audio.url);
                                    setAudioTitle(audio.position + " " + audio.title || t("unnamed"));
                                    setSimulatorMediaType(audio.mediaType ?? "AUDIO");
                                }
                            });
                        }
                        // isNoMediaPoint: consumed only (section advanced by shouldPlay),
                        // nothing to play — no lock taken.
                    }
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [combinedWaypoints, selectedLanguage, ensureAudioPrefetched, t],
    );

    // --- Playback controls and animation (constant speed across entire route)
    const prepareRoutePoints = useCallback((): L.LatLng[] => {
        if (polylineDecoded && polylineDecoded.length > 1 && trackData?.isWithoutRoutes === false) {
            return polylineDecoded;
        }
        // Fallback to straight line between waypoint points
        const pts = combinedWaypoints
            .filter((w: any) => typeof w.latitude === "number" && typeof w.longitude === "number")
            .sort((a: any, b: any) => a.position - b.position)
            .map((w: any) => L.latLng(w.latitude as number, w.longitude as number));
        return pts;
    }, [polylineDecoded, combinedWaypoints, trackData?.isWithoutRoutes]);

    const computeCumulativeDistances = useCallback((pts: L.LatLng[]) => {
        const cum: number[] = [0];
        for (let i = 1; i < pts.length; i++) {
            const segKm = pts[i - 1].distanceTo(pts[i]) / 1000;
            cum.push(cum[i - 1] + segKm);
        }
        cumDistancesRef.current = cum;
        totalDistanceKmRef.current = cum[cum.length - 1] || 0;
    }, []);

    const handleReset = useCallback(() => {
        const pts = prepareRoutePoints();
        polylinePointsRef.current = pts;
        computeCumulativeDistances(pts);
        searchIdxRef.current = 1;
        playStartTimeRef.current = 0;
        pausedDistanceKmRef.current = 0;
        currentDistanceKmRef.current = 0;
        if (pts && pts.length > 0) setBusPosition(pts[0]);
        currentSectionRef.current = 0;
        setCurrentSection(0);
        triggeredIdsRef.current.clear();
        audioUrlCacheRef.current.clear();
        audioPrefetchInFlightRef.current.clear();
        mediaBusyRef.current = false;
        setMp3File(null);
        setAudioTitle("");
        setSimulatorMediaType("AUDIO");
        setIsPlaying(false);
    }, [prepareRoutePoints, computeCumulativeDistances]);

    // Release the media lock when the current waypoint media ends (or is closed),
    // so the next content waypoint can trigger if the bus is still in its bubble.
    const handleMediaEnded = useCallback(() => {
        mediaBusyRef.current = false;
    }, []);

    const handlePlayPause = useCallback(() => {
        if (isPlaying) {
            // Pause and save current distance; cancel next frame
            setIsPlaying(false);
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            pausedDistanceKmRef.current = currentDistanceKmRef.current;
            playStartTimeRef.current = 0;
            return;
        }
        if (!selectedLanguage) {
            window.alert(t("pleaseSelectLanguage"));
            return;
        }

        // If no route prepared yet, prepare
        if (!polylinePointsRef.current || polylinePointsRef.current.length < 2) {
            const pts = prepareRoutePoints();
            if (!pts || pts.length < 2) {
                window.alert(t("notEnoughPoints"));
                return;
            }
            polylinePointsRef.current = pts;
            computeCumulativeDistances(pts);
            searchIdxRef.current = 1;
            playStartTimeRef.current = 0;
            pausedDistanceKmRef.current = 0;
            currentDistanceKmRef.current = 0;
            setBusPosition(pts[0]);
            currentSectionRef.current = 0;
            setCurrentSection(0);
            triggeredIdsRef.current.clear();
            audioUrlCacheRef.current.clear();
            audioPrefetchInFlightRef.current.clear();
            mediaBusyRef.current = false;
            setMp3File(null);
            setAudioTitle("");
            setSimulatorMediaType("AUDIO");
        }

        setIsPlaying(true);
    }, [isPlaying, prepareRoutePoints, computeCumulativeDistances, t, selectedLanguage]);

    // Keep speed changes smooth by resetting the time origin while preserving distance
    useEffect(() => {
        if (!isPlaying) return;
        pausedDistanceKmRef.current = currentDistanceKmRef.current;
        playStartTimeRef.current = 0;
    }, [busSpeed, isPlaying]);

    useEffect(() => {
        if (!isPlaying) return;
        const pts = polylinePointsRef.current;
        if (!pts || pts.length < 2) return;
        if (busSpeed <= 0) return;

        const step = (timestamp: number) => {
            if (!playStartTimeRef.current) playStartTimeRef.current = timestamp;
            const elapsedMs = timestamp - playStartTimeRef.current;
            const distanceKm = pausedDistanceKmRef.current + (busSpeed * elapsedMs) / 3600000; // km
            currentDistanceKmRef.current = distanceKm;

            const totalKm = totalDistanceKmRef.current;
            if (distanceKm >= totalKm) {
                setBusPosition(pts[pts.length - 1]);
                setIsPlaying(false);
                return;
            }

            // Find segment index using advancing cursor
            let i = searchIdxRef.current;
            const cum = cumDistancesRef.current;
            while (i < cum.length && cum[i] < distanceKm) i++;
            const idx = Math.max(1, Math.min(i, cum.length - 1));
            searchIdxRef.current = idx;

            const segStartKm = cum[idx - 1];
            const segEndKm = cum[idx];
            const segLenKm = Math.max(1e-9, segEndKm - segStartKm);
            const tSeg = (distanceKm - segStartKm) / segLenKm;

            const a = pts[idx - 1];
            const b = pts[idx];
            const lat = a.lat + (b.lat - a.lat) * tSeg;
            const lng = a.lng + (b.lng - a.lng) * tSeg;
            const pos = L.latLng(lat, lng);
            setBusPosition(pos);
            triggerNextWaypointAudio(pos);

            if (followBus && map) {
                map.panTo(pos, { animate: false });
            }

            animationRef.current = requestAnimationFrame(step);
        };

        animationRef.current = requestAnimationFrame(step);

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isPlaying, busSpeed, triggerNextWaypointAudio, followBus, map]);

    const busDivIcon = L.divIcon({
        className: "bus-div-icon",
        html: '<div style="width:18px;height:18px;border-radius:50%;background:#e74c3c;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });

    const handleExportWaypoints = useCallback(() => {
        const languageMappings = ((data as any)?.languagesAI || []).map((lang: DropdownOption & { code?: string }) => ({
            id: lang.value,
            code: lang.code || lang.value,
        }));

        const exportRows = buildWaypointExportRows(waypoints, languageMappings);

        const trackLabel = (trackData as any)?.title || trackData?.trackId || "track";
        const safeName = String(trackLabel)
            .replace(/[^a-z0-9-_]+/gi, "_")
            .slice(0, 50);

        setExportingWaypoints(true);
        try {
            exportWaypointsToExcel({
                waypoints: exportRows,
                languageMappings,
                filename: `waypoints-${safeName}.xlsx`,
            });
        } finally {
            setExportingWaypoints(false);
        }
    }, [waypoints, data, trackData]);

    const setStartFromWaypoint = useCallback(
        (waypointId: string) => {
            setStartWaypointId(waypointId);
            // Ensure route is ready
            if (!polylinePointsRef.current || polylinePointsRef.current.length < 2) {
                const pts = prepareRoutePoints();
                if (!pts || pts.length < 2) return;
                polylinePointsRef.current = pts;
                computeCumulativeDistances(pts);
            }
            const pts = polylinePointsRef.current;
            const cum = cumDistancesRef.current;
            const wpt = (combinedWaypoints as any[]).find((w) => (w.id ?? w.internalId) === waypointId);
            const lat = wpt?.latitude ?? wpt?.point?.lat;
            const lng = wpt?.longitude ?? wpt?.point?.lng;
            if (lat == null || lng == null) return;
            const target = L.latLng(lat, lng);

            let bestDistance = Number.POSITIVE_INFINITY;
            let bestAlongKm = 0;
            let bestPos: L.LatLng | null = null;
            for (let i = 1; i < pts.length; i++) {
                const a = pts[i - 1];
                const b = pts[i];
                const abx = b.lng - a.lng;
                const aby = b.lat - a.lat;
                const apx = target.lng - a.lng;
                const apy = target.lat - a.lat;
                const ab2 = abx * abx + aby * aby || 1e-9;
                let tProj = (apx * abx + apy * aby) / ab2;
                tProj = Math.max(0, Math.min(1, tProj));
                const projLat = a.lat + (b.lat - a.lat) * tProj;
                const projLng = a.lng + (b.lng - a.lng) * tProj;
                const proj = L.latLng(projLat, projLng);
                const dist = proj.distanceTo(target);
                if (dist < bestDistance) {
                    bestDistance = dist;
                    const segKm = Math.max(1e-9, cum[i] - cum[i - 1]);
                    bestAlongKm = cum[i - 1] + segKm * tProj;
                    bestPos = proj;
                }
            }
            pausedDistanceKmRef.current = bestAlongKm;
            currentDistanceKmRef.current = bestAlongKm;
            if (bestPos) setBusPosition(bestPos);
            // Adjust current section to the chosen waypoint's section
            const withSection = combinedWaypoints as unknown as MergedWaypointWithSection[];
            const chosen = withSection.find((w) => (w as any).id === waypointId);
            if (chosen && typeof chosen.section === "number") {
                currentSectionRef.current = chosen.section;
                setCurrentSection(chosen.section);
            }
            // Reset triggers so audios can play from here onwards
            triggeredIdsRef.current.clear();
            audioUrlCacheRef.current.clear();
            audioPrefetchInFlightRef.current.clear();
            mediaBusyRef.current = false;
            setMp3File(null);
            setAudioTitle("");
            setSimulatorMediaType("AUDIO");
        },
        [prepareRoutePoints, computeCumulativeDistances, combinedWaypoints],
    );

    return (
        <div className={`content-container experience-map ${shell.mapShell}`}>
            <div className={`${mapStyles.leftColumn}${mp3File ? ` ${mapStyles.leftColumnWithPlayer}` : ""}`}>
                <div className={`page-title experience-map-title ${mapStyles.titleBlock}`}>
                    <TitleSubtitle
                        title={t("mapAndContent")}
                        subtitle={t("adjustTheItineraryAndTheContentOfYourAudioTourHere")}
                        layout="map"
                    />
                </div>

                <div className={mapStyles.mapContainer}>
                    <div className="map-placeholder">
                        <MapContainer
                            center={[1, 1]}
                            zoom={16}
                            style={{ height: "480px", width: "100%" }}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                        >
                            <MapInitializer onMapReady={handleMapReady} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            {!polylineDecoded && trackData?.isWithoutRoutes === false && (
                                <Polyline
                                    positions={combinedWaypoints
                                        ?.filter((w: any) => w.latitude && w.longitude)
                                        .map((waypoint: any) => [waypoint.latitude, waypoint.longitude])}
                                    pathOptions={{ color: "#1930d8", weight: 4, opacity: 0.85 }}
                                />
                            )}
                            {polylineDecoded && trackData?.isWithoutRoutes === false && (
                                <Polyline
                                    positions={polylineDecoded}
                                    pathOptions={{ color: "#1930d8", weight: 4, opacity: 0.85 }}
                                />
                            )}
                            {combinedWaypoints?.map((waypoint: any) => {
                                if (!waypoint.latitude || !waypoint.longitude) return null;

                                const markerType = [
                                    "GARDEN",
                                    "INVISIBLE",
                                    "MOBILE",
                                    "FIXED",
                                    "QUIZ",
                                    "SELECTED",
                                    "STOP",
                                ].includes(waypoint.type)
                                    ? (waypoint.type as MarkerType)
                                    : undefined;

                                return (
                                    <CustomMarker
                                        key={waypoint.id}
                                        id={waypoint.id}
                                        lat={waypoint.latitude}
                                        lng={waypoint.longitude}
                                        type={markerType}
                                        position={waypoint.position}
                                        onSelect={() => navigateToNext(waypoint.id)}
                                        onMarkerDragEnd={handleMarkerDragEnd}
                                        circleRadius={waypoint.startDistance || 0}
                                        circleColor={waypoint.isOverlapping ? "255, 0, 0" : "25, 48, 216"}
                                    />
                                );
                            })}

                            {busPosition && <Marker position={busPosition} icon={busDivIcon} />}
                        </MapContainer>
                    </div>
                </div>

                {/* Simulator */}
                <div className={mapStyles.simulatorToggleRow}>
                    <button
                        type="button"
                        className={`${mapStyles.simToggle}${showSimulator ? ` ${mapStyles.simToggleOpen}` : ""}`}
                        onClick={() => setShowSimulator((s) => !s)}
                        aria-expanded={showSimulator}
                        aria-controls="map-simulator-panel"
                    >
                        <Icon name="ic-chevron-down" className="ic chevron" />
                        <span className={mapStyles.simToggleLabel}>
                            {showSimulator
                                ? t("hideSimulator") || "Hide simulator"
                                : t("showSimulator") || "Show simulator"}
                        </span>
                    </button>
                </div>
                {showSimulator && (
                    <div id="map-simulator-panel" className={mapStyles.simulatorPanel}>
                        <div className={mapStyles.simulatorControls}>
                            <div className={mapStyles.simBlock}>
                                <div className={mapStyles.simLabel}>{t("selectALanguage")}</div>
                                <Dropdown
                                    options={filteredLanguageOptions}
                                    placeholder={t("selectALanguage")}
                                    value={selectedLanguage || ""}
                                    onChange={(value) => setSelectedLanguage(value)}
                                    label={""}
                                    loading={false}
                                    error={false}
                                    isWithoutMargin
                                />
                            </div>

                            <div className={`${mapStyles.simBlock} ${mapStyles.simMiddle}`}>
                                <div className={mapStyles.simLabel}>{t("enterBusSpeed")}</div>
                                <Slider
                                    min={5}
                                    max={100}
                                    step={5}
                                    value={busSpeed}
                                    onChange={(v) => setBusSpeed(Array.isArray(v) ? v[0] : v)}
                                />
                                <div className={mapStyles.simSpeedInput}>
                                    <input
                                        type="number"
                                        min={1}
                                        max={200}
                                        step={1}
                                        value={busSpeed}
                                        onChange={(e) =>
                                            setBusSpeed(Math.max(1, Math.min(200, Number(e.target.value) || 0)))
                                        }
                                        className={mapStyles.simInputNumber}
                                    />
                                    <span className={mapStyles.simInputSuffix}>km/h</span>
                                </div>
                            </div>

                            <div className={`${mapStyles.simBlock} ${mapStyles.simRight}`}>
                                <div className={mapStyles.simLabel}>{t("startPosition")}</div>
                                <Dropdown
                                    label=""
                                    options={combinedWaypoints.map((w: any) => ({
                                        value: w.id,
                                        label: `#${w.position} ${w.internalName || t("unnamed") || "Unnamed"}`,
                                    }))}
                                    onChange={(id: string) => setStartFromWaypoint(id)}
                                    loading={false}
                                    error={false}
                                    value={startWaypointId}
                                    placeholder={t("selectStartingPoint") || "Select starting point"}
                                    isWithoutMargin
                                />
                            </div>

                            <div className={mapStyles.simBlock}>
                                <div className={mapStyles.simLabel}>{t("movementControl")}</div>
                                <div className={mapStyles.simActions}>
                                    <button
                                        type="button"
                                        className={mapStyles.simRnd}
                                        onClick={handlePlayPause}
                                        aria-label={isPlaying ? t("pause") || "Pause" : t("play") || "Play"}
                                    >
                                        <Icon name={isPlaying ? "ic-pause" : "ic-play"} className="ic" />
                                    </button>
                                    <button
                                        type="button"
                                        className={mapStyles.simRnd}
                                        onClick={handleReset}
                                        aria-label={t("stop") || "Stop"}
                                    >
                                        <Icon name="ic-square" className="ic" />
                                    </button>
                                    <Button
                                        label={followBus ? t("followOff") : t("followOn")}
                                        onClick={() => setFollowBus((f) => !f)}
                                        variant="primary"
                                        shape="pill"
                                        className={mapStyles.simFollow}
                                    />
                                </div>
                            </div>
                            <div className={mapStyles.simStatus}>
                                {t("currentSectionInfo")} <b>{currentSection}</b>
                            </div>
                        </div>
                    </div>
                )}

                <div className={`experience-map-add ${mapStyles.add}`}>
                    <h3 className={`experience-map-add-title ${mapStyles.addTitle}`}>{t("addANewWaypoint")}</h3>
                    <div className={`experience-map-add-grid ${mapStyles.addGrid}`}>
                        <div className={`add-waypoint-form experience-map-add-fields ${mapStyles.addFields}`}>
                            <div className="add-waypoint-coordinates">
                                <TextInput
                                    placeholder={t("enterCoordinatesLatLng")}
                                    value={coordinates}
                                    onChange={(e) => setCoordinates(e.target.value)}
                                    isMissing={showErrors && fieldErrors.coordinates}
                                />
                            </div>
                            <TextInput
                                placeholder={t("enterPosition")}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                isMissing={showErrors && fieldErrors.position}
                            />
                            <Dropdown
                                label=""
                                options={dropdownOptionsWaypointType}
                                onChange={(value: string) => setWptType(value)}
                                placeholder={t("selectType")}
                                value={wptType || ""}
                                isMissing={showErrors && fieldErrors.wptType}
                                loading={false}
                                error={false}
                            />
                            <Button
                                label={t("add")}
                                onClick={handleAddWaypoint}
                                variant="primary"
                                shape="pill"
                                className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                            />
                        </div>
                        <div className={`experience-map-actions ${mapStyles.actions}`}>
                            <Button
                                label={t("reset")}
                                onClick={rsetTrack}
                                variant="outline"
                                shape="pill"
                                className={mapStyles.mapBtn}
                            />
                            <Button
                                label={loadingSave ? t("loading") : t("applyChanges")}
                                onClick={saveModificationToTrack}
                                variant="primary"
                                shape="pill"
                                className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                            />
                            {hasUnsavedChanges && (
                                <p className={`experience-map-pending ${mapStyles.pending}`}>
                                    <Icon name="ic-alert-triangle" className="ic" />
                                    <span>{t("unsavedChanges")}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={`right-sidebar experience-map-poi ${mapStyles.poiColumn}`}>
                <div className="waypoints-list">
                    <div className={`waypoints-list-header ${mapStyles.waypointsHeader}`}>
                        <h3>{t("waypoints")}</h3>
                        <Tooltip title={t("exportWaypointsExcelTooltip")} arrow placement="left">
                            <span>
                                <ExportButton
                                    label={t("exportWaypointsExcel")}
                                    loadingLabel={t("exporting")}
                                    onClick={handleExportWaypoints}
                                    disabled={exportingWaypoints || waypoints.length === 0}
                                    loading={exportingWaypoints}
                                />
                            </span>
                        </Tooltip>
                    </div>
                    <WaypointList
                        combinedWaypoints={combinedWaypoints}
                        setCombinedWaypoints={setCombinedWaypoint}
                        saveTemporaryWaypoint={saveTemporaryWaypoint}
                        deleteWaypoint={(id: string) => deleteWaypoint(id)}
                        navigateToNext={navigateToNext}
                        isLoading={loadingSave}
                        refreshWaypoints={refreshWaypoints}
                        areWaypointsEditable
                        onUnsavedChange={onUnsavedChange}
                    />
                </div>
            </div>
            {mp3File && (
                <div className={audioFooter.sectionMap}>
                    <HorizontalSeparator />
                    {simulatorMediaType === "VIDEO" ? (
                        <VideoPlayerRewind key={mp3File} src={mp3File} isAutoPlay={true} onEnded={handleMediaEnded} />
                    ) : (
                        <AudioPlayerRewind
                            title={`#${position} ${audioTitle}` || t("unnamed") || ""}
                            src={mp3File}
                            isAutoPlay={true}
                            onEnded={handleMediaEnded}
                        />
                    )}
                </div>
            )}

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${stepStyles.footerMap}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                    />
                </div>
            )}
        </div>
    );
};

export default MapContent;
